import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

export default function ClientPrograms() {
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null); // hydrated enrollment
  const [idx, setIdx] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [journal, setJournal] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("kcis_client_token")) { nav("/client/login"); return; }
    (async () => {
      try { const { data } = await api.get("/programs/my/enrollments"); setList(data.enrollments || []); }
      catch { setList([]); }
    })();
  }, [nav]);

  const open = async (eid) => {
    try {
      const { data } = await api.get(`/programs/my/enrollments/${eid}`);
      const e = data.enrollment;
      setActive(e);
      setIdx(e.current_module_index || 0);
      setCompleted(e.completed_module_ids || []);
      setJournal("");
    } catch { setMsg("Could not open program"); }
  };

  const modules = active?.template?.modules || [];
  const mod = modules[idx];

  const persist = async (nextIdx, nextCompleted, status) => {
    try {
      const { data } = await api.put(`/programs/my/enrollments/${active.id}/progress`, {
        current_module_index: nextIdx, completed_module_ids: nextCompleted, status,
      });
      setActive((a) => ({ ...a, ...data.enrollment, template: a.template }));
    } catch { setMsg("Save failed"); }
  };

  const completeModule = async () => {
    const nc = Array.from(new Set([...completed, mod.id]));
    setCompleted(nc);
    const nextIdx = Math.min(idx + 1, modules.length - 1);
    setIdx(nextIdx);
    await persist(nextIdx, nc);
    setJournal("");
  };
  const goto = async (n) => { const ni = Math.max(0, Math.min(n, modules.length - 1)); setIdx(ni); await persist(ni, completed); };

  if (list === null) return <Shell><p className="text-sm text-slate-500">Loading…</p></Shell>;

  if (!active) {
    return (
      <Shell>
        <h1 className="font-display text-2xl text-slate-900 mb-4">My Programs</h1>
        {msg && <div className="mb-3 text-sm text-purple-900">{msg}</div>}
        {list.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-600" data-testid="programs-empty">
            You have no active programs yet. Programs assigned by your care team will appear here.
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-programs-list">
            {list.map((e) => (
              <div key={e.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4" data-testid={`enrollment-${e.id}`}>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">{e.template_title}</div>
                  <div className="text-xs text-slate-500">{e.status} · {e.progress_pct}% complete · {e.module_count} modules</div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full"><div className="h-1.5 rounded-full bg-purple-800" style={{ width: `${e.progress_pct}%` }} /></div>
                </div>
                <button onClick={() => open(e.id)} className="rounded-full bg-purple-900 text-white text-sm px-4 py-2" data-testid={`continue-${e.id}`}>Continue Program</button>
              </div>
            ))}
          </div>
        )}
      </Shell>
    );
  }

  return (
    <Shell>
      <button onClick={() => setActive(null)} className="text-xs text-purple-900 hover:underline mb-3">← My Programs</button>
      <h1 className="font-display text-2xl text-slate-900">{active.template?.title}</h1>
      <div className="text-xs text-slate-500 mt-1">Module {idx + 1} of {modules.length} · {active.progress_pct}% complete</div>
      <div className="mt-2 h-1.5 bg-slate-100 rounded-full"><div className="h-1.5 rounded-full bg-purple-800" style={{ width: `${active.progress_pct}%` }} /></div>

      {mod ? (
        <div className="bg-white rounded-xl border border-slate-200 p-6 mt-5" data-testid="runner-module">
          <div className="text-lg font-semibold text-slate-900">{mod.title}</div>
          {mod.content && <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{mod.content}</p>}
          {mod.tasks?.length > 0 && <Section t="Action Steps"><ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">{mod.tasks.map((x,i)=><li key={i}>{x}</li>)}</ul></Section>}
          {mod.checkins?.length > 0 && <Section t="Check-In"><ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">{mod.checkins.map((x,i)=><li key={i}>{x}</li>)}</ul></Section>}
          {mod.reflections?.length > 0 && <Section t="Reflection"><ul className="list-disc pl-5 text-sm text-slate-700 space-y-1 mb-2">{mod.reflections.map((x,i)=><li key={i}>{x}</li>)}</ul><textarea className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" rows={3} placeholder="Your reflection (private)…" value={journal} onChange={(e)=>setJournal(e.target.value)} /></Section>}
          {mod.practitioner_checkpoint && <div className="mt-4 text-xs bg-amber-50 text-amber-800 rounded px-3 py-2">Practitioner checkpoint — your care team will review before you continue.</div>}
          <div className="mt-6 flex items-center gap-2">
            <button onClick={() => goto(idx - 1)} disabled={idx === 0} className="rounded-full border border-slate-300 text-sm px-4 py-2 disabled:opacity-40">Previous</button>
            {idx < modules.length - 1
              ? <button onClick={completeModule} className="rounded-full bg-purple-900 text-white text-sm px-5 py-2" data-testid="runner-complete">Mark Complete & Next</button>
              : <button onClick={completeModule} className="rounded-full bg-emerald-700 text-white text-sm px-5 py-2" data-testid="runner-finish">Complete Program</button>}
          </div>
        </div>
      ) : <p className="mt-5 text-sm text-slate-500">This program has no modules yet.</p>}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="font-display text-lg text-slate-900">KISSED Client Portal · Programs</span>
          <Link to="/client/dashboard" className="text-xs text-purple-900 hover:underline">← My Care Hub</Link>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-8" data-testid="client-programs">{children}</main>
    </div>
  );
}
function Section({ t, children }) {
  return <div className="mt-4"><div className="text-xs font-semibold uppercase tracking-widest text-purple-900">{t}</div><div className="mt-1">{children}</div></div>;
}
